/* One dropdown at every breakpoint, with section-editor cleanup. */
(() => {
  window.GrovaSections.register('grova-header', '[data-grova-header]', (root, scope) => {
    const toggle = root.querySelector('[data-header-toggle]');
    const menu = root.querySelector('[data-header-menu]');
    const backdrop = root.parentElement.querySelector('[data-header-backdrop]');
    const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Measure the actual row (including wrapped labels), not the dropdown.
    const section = root.parentElement;
    const bar = root.querySelector('.grova-header__bar');
    if (bar && window.ResizeObserver) {
      const rowObserver = new ResizeObserver(() => {
        section.style.setProperty('--grova-header-row-height', `${bar.getBoundingClientRect().height}px`);
      });
      rowObserver.observe(bar);
      scope.cleanup(() => {
        rowObserver.disconnect();
        section.style.removeProperty('--grova-header-row-height');
      });
    }
    let open = false;
    let animation;
    let lastScroll = Math.max(0, window.scrollY);
    let scrollFrame = 0;
    let releaseFrame = 0;
    let suspended = false;
    toggle.hidden = false;
    root.classList.toggle('is-scrolled', lastScroll > 8);

    // The header's section remains in normal flow after the announcement bar.
    // Follow its visible top only until it scrolls away; never bring the bar back.
    function syncFlowOffset() {
      if (suspended || document.body.dataset.gnpScrollLocked === 'true' ||
          document.documentElement.classList.contains('grova-cart-open')) return;
      const offset = Math.max(0, section.getBoundingClientRect().top);
      root.style.setProperty('--grova-header-flow-offset', `${offset}px`);
      root.classList.add('is-positioned');
    }
    const announcementObserver = window.ResizeObserver ? new ResizeObserver(syncFlowOffset) : null;
    function observeAnnouncements() {
      announcementObserver?.disconnect();
      document.querySelectorAll('.announcement-bar-section').forEach(announcement => {
        if (announcement.compareDocumentPosition(section) & Node.DOCUMENT_POSITION_FOLLOWING) {
          announcementObserver?.observe(announcement);
        }
      });
      syncFlowOffset();
    }
    let announcementFrame = 0;
    function scheduleAnnouncements() {
      cancelAnimationFrame(announcementFrame);
      announcementFrame = requestAnimationFrame(observeAnnouncements);
    }
    observeAnnouncements();
    scope.on(window, 'resize', syncFlowOffset, { passive: true });
    ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach(name => {
      scope.on(document, name, scheduleAnnouncements);
    });
    scope.cleanup(() => {
      announcementObserver?.disconnect();
      cancelAnimationFrame(announcementFrame);
      root.classList.remove('is-positioned');
      root.style.removeProperty('--grova-header-flow-offset');
    });

    function setOpen(value, restoreFocus = false) {
      if (open === value) return;
      open = value;
      backdrop?.classList.toggle('is-open', open);
      animation?.cancel();
      toggle.setAttribute('aria-expanded', String(open));
      toggle.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
      menu.inert = !open;
      if (restoreFocus) toggle.focus({ preventScroll: true });
      if (open) {
        root.classList.remove('is-hidden');
        menu.hidden = false;
      }
      if (!menu.animate || reducedMotion()) {
        menu.hidden = !open;
        return;
      }
      const frames = [{ opacity: 0, transform: 'translateY(-10px)' }, { opacity: 1, transform: 'translateY(0)' }];
      const current = menu.animate(open ? frames : frames.slice().reverse(), { duration: 220, easing: 'cubic-bezier(.22,.61,.36,1)' });
      animation = current;
      current.onfinish = () => {
        if (animation !== current) return;
        menu.hidden = !open;
        animation = null;
      };
    }
    scope.on(toggle, 'click', () => setOpen(!open));
    // Keep the backdrop in the hit-test path for the entire tap. Dismissing on
    // pointerdown lets mobile browsers send the subsequent click to the page.
    scope.on(window, 'pointerdown', (event) => {
      if (!open || root.contains(event.target)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    }, { capture: true });
    scope.on(window, 'click', (event) => {
      if (!open || root.contains(event.target)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      setOpen(false, true);
    }, { capture: true });
    scope.on(document, 'keydown', (event) => {
      if (event.key === 'Escape' && open) {
        event.preventDefault();
        setOpen(false, true);
      }
    });
    scope.on(root, 'focusout', (event) => {
      if (open && event.relatedTarget && !root.contains(event.relatedTarget)) setOpen(false);
    });
    scope.on(root, 'focusin', () => root.classList.remove('is-hidden'));
    function setGroupOpen(button, expanded) {
      menu.querySelectorAll('[data-header-group-toggle]').forEach((group) => {
        if (group !== button && !expanded) return;
        const active = group === button && expanded;
        group.setAttribute('aria-expanded', String(active));
        group.nextElementSibling.inert = !active;
      });
    }
    scope.on(menu, 'click', (event) => {
      const button = event.target.closest('[data-header-group-toggle]');
      if (button) {
        setGroupOpen(button, button.getAttribute('aria-expanded') !== 'true');
      } else if (event.target.closest('a')) setOpen(false);
    });

    // Cart uses a document-level capture handler, so observe its actual state
    // instead of competing for its click or changing its scroll-lock behavior.
    const cart = document.querySelector('.grova-cart');
    const nutrition = document.querySelector('.grova-nutrition-drawer');
    const drawerIsOpen = () => document.body.dataset.gnpScrollLocked === 'true' ||
      document.documentElement.classList.contains('grova-cart-open') || nutrition?.open;
    function syncDrawers() {
      cancelAnimationFrame(releaseFrame);
      if (drawerIsOpen()) {
        suspended = true;
        setOpen(false);
      } else {
        // Discard scroll events from restoring the cart's fixed-body lock.
        releaseFrame = requestAnimationFrame(() => {
          releaseFrame = requestAnimationFrame(() => {
            lastScroll = Math.max(0, window.scrollY);
            suspended = false;
            reconcileTop();
          });
        });
      }
    }
    const observer = new MutationObserver(syncDrawers);
    observer.observe(document.body, { attributes: true, attributeFilter: ['data-gnp-scroll-locked'] });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    if (cart) observer.observe(cart, { attributes: true, attributeFilter: ['class'] });
    if (nutrition) observer.observe(nutrition, { attributes: true, attributeFilter: ['open'] });
    let layoutAdjusting = false;
    let layoutReleaseFrame = 0;
    let layoutReleaseTimer;
    function reconcileTop() {
      if (suspended || drawerIsOpen()) return;
      const y = Math.max(0, window.scrollY);
      syncFlowOffset();
      root.classList.toggle('is-scrolled', y > 8);
      if (y < 60) root.classList.remove('is-hidden');
    }
    function releaseLayout() {
      clearTimeout(layoutReleaseTimer);
      cancelAnimationFrame(layoutReleaseFrame);
      lastScroll = Math.max(0, window.scrollY);
      layoutAdjusting = false;
      reconcileTop();
    }
    scope.on(document, 'grova:layout-adjustment', (event) => {
      cancelAnimationFrame(layoutReleaseFrame);
      clearTimeout(layoutReleaseTimer);
      layoutAdjusting = true;
      // Layout suppression is temporary, even if an interrupted update misses its end event.
      layoutReleaseTimer = setTimeout(releaseLayout, 600);
      if (!event.detail.active) {
        layoutReleaseFrame = requestAnimationFrame(() => {
          layoutReleaseFrame = requestAnimationFrame(releaseLayout);
        });
      }
    });
    scope.on(window, 'scroll', () => {
      if (scrollFrame) return;
      scrollFrame = requestAnimationFrame(() => {
        scrollFrame = 0;
        const y = Math.max(0, window.scrollY);
        if (suspended || drawerIsOpen()) { lastScroll = y; return; }
        // Returning to the top must reveal navigation, including during offer settling.
        reconcileTop();
        if (layoutAdjusting) { lastScroll = y; return; }
        if (open) { lastScroll = y; return; }
        if (y < 60) root.classList.remove('is-hidden');
        else if (Math.abs(y - lastScroll) < 8) return;
        else root.classList.toggle('is-hidden', y > lastScroll);
        lastScroll = y;
      });
    }, { passive: true });
    scope.on(document, 'shopify:section:select', (event) => {
      if (event.target.contains(root)) { root.classList.remove('is-hidden'); setOpen(true); }
    });
    scope.on(document, 'shopify:block:select', (event) => {
      if (!root.contains(event.target)) return;
      setOpen(true);
      const button = event.target.querySelector('[data-header-group-toggle]');
      if (button) setGroupOpen(button, true);
    });
    scope.cleanup(() => {
      backdrop?.classList.remove('is-open');
      observer.disconnect();
      clearTimeout(layoutReleaseTimer);
      cancelAnimationFrame(layoutReleaseFrame);
      cancelAnimationFrame(scrollFrame);
      cancelAnimationFrame(releaseFrame);
      animation?.cancel();
    });
  });
})();
